import { createSlice } from "@reduxjs/toolkit";
import {
  GetCustodianAccountTable,
  GetFileInfo,
  GetUserKyc,
  GetUserStatusTable,
  UserStatusChange,
  GetAllUsersName,
  GetUserCustodian,
  UpdateUserCustodian,
  RegisterUser,
  GetCustodianTable,
  UserKycChange,
  GetUserCustodianById,
  AddCustodians,
  CreateKYC,
  DeleteKYC,
  GetUsers,
  GetApiPermission,
  GetUserInfo,
  GetUserRoles,
  GetAllRoles,
  CreateSubRoleGroup,
  GetRoleGroups,
  GetRoleGroupPermissions,
  UpdateRoleGroupPermissions,
  GetHouseholds,
} from "../Actions/userStatusService";
import customFailAlert from "../../Alerts/customFailAlert";
import customSuccessAlert from "../../Alerts/customSuccessAlert";

const initialState = {
  error: "",
  code: "",
  status: "",
  loading: false,
  userData: [],
  userDataCount: 0,
  userDataSkip: 0,
  userDataTake: 20,
  userKyc: {},
  custAccData: [],
  userCust: [],
  userCustodians: [],
  userNames: [],
  allRoles: [],
  roleGroups: [],
  roleGroupPermissions: [],
  households: [],
  householdsLoading: false,
};

const userStatusReducer = createSlice({
  name: "userStatus",
  initialState,
  reducers: {},
  extraReducers: {
    [GetUserStatusTable.pending]: (state, action) => {
      state.loading = true;
    },
    [GetUserStatusTable.fulfilled]: (
      state,
      { payload, meta }
    ) => {
      state.loading = false;
      const statusCode = payload?.statusCode || payload?.code;
      const data = payload?.data || payload;
      const message = payload?.message;
      
      if (statusCode === 200 || statusCode === 201) {
        // Handle different possible response structures
        const userRoles = Array.isArray(data?.userRoles) 
          ? data.userRoles 
          : Array.isArray(data) 
          ? data 
          : [];
        state.userData = userRoles;
        state.userDataCount = data?.count || userRoles.length;
        // Store pagination parameters from the request
        const skip = meta?.arg?.skip || 0;
        const take = meta?.arg?.take || 20;
        state.userDataSkip = skip;
        state.userDataTake = take;
        state.message = message;
        state.code = statusCode;
      } else {
        state.userData = [];
        state.userDataCount = 0;
        state.error = message;
        state.code = statusCode;
      }
    },
    [GetUserStatusTable.rejected]: (state, action) => {
      state.loading = false;
    },

    [RegisterUser.pending]: (state, action) => {
      state.loading = true;
    },
    [RegisterUser.fulfilled]: (
      state,
      { payload: { status, data, message, code } }
    ) => {
      state.loading = false;
      if ((code === 1 || status?.toUpperCase() === "SUCCESS") && data) {
        customSuccessAlert(message);

        const index = state.userData?.findIndex(
          (item) => item?.userId == data?.id
        );

        if (index === -1) {
          const username = `${data?.userDetailsDto?.firstName} ${data?.userDetailsDto?.lastName}`;

          const roles =
            typeof data?.role === "string" ? [data?.role] : data?.role;

          data.roles = roles;

          state.userData.push({
            ...data,
            username,
          });
        }

        state.message = message;
        state.status = status;
        state.code = code;
      } else {
        customFailAlert(message);
        state.error = message;
        state.status = status;
        state.code = code;
      }
    },
    [RegisterUser.rejected]: (state, action) => {
      customFailAlert(action?.error?.message);
      state.loading = false;
    },

    [UserStatusChange.pending]: (state) => {
      state.loading = true;
    },
    [UserStatusChange.fulfilled]: (state, { payload }) => {
      state.loading = false;

      const { code, status, message, data } = payload || {};

      if ((code === 200 || status === "SUCCESS") && data) {
        customSuccessAlert(message);

        const { userId, userStatus } = data;

        const userIndex = state.userData.findIndex(
          (user) => user.userId === userId
        );

        if (userIndex !== -1) {
          const updatedUser = {
            ...state.userData[userIndex],
            userStatus: userStatus,
          };

          state.userData[userIndex] = updatedUser;
        }
      } else {
        customFailAlert(message);
        state.error = message;
        state.status = status;
        state.code = code;
      }
    },
    [UserStatusChange.rejected]: (state) => {
      state.loading = false;
    },

    [UserKycChange.pending]: (state) => {
      state.loading = true;
    },
    [UserKycChange.fulfilled]: (state, { payload }) => {
      state.loading = false;
      const { statusCode, message, data } = payload || {};

      if (statusCode === 200) {
        customSuccessAlert(message);
        // const { _id, status } = data?.usersWithStatus;

        // const userIndex = state.userData?.usersWithStatus.findIndex(
        //   (user) => user?.kycStatus?._id == _id
        // );
        //
        // if (userIndex !== -1) {
        //   const updatedUser = {
        //     ...state.userData?.usersWithStatus[userIndex],
        //     kycStatus: {
        //       ...state.userData?.usersWithStatus[userIndex].kycStatus,
        //       status: status,
        //     },
        //   };

        //   state.userData[userIndex] = updatedUser;
        //  }
      } else {
        customFailAlert(message);
        state.error = message;
        state.status = statusCode;
      }
    },

    [UserKycChange.rejected]: (state) => {
      state.loading = false;
    },

    [GetUserCustodianById.pending]: (state, action) => {
      state.loading = true;
    },
    [GetUserCustodianById.fulfilled]: (
      state,
      { payload: { data, message, statusCode } }
    ) => {
      state.loading = false;
      if (statusCode === 200) {
        state.userCustodians = data;
        state.message = message;
        state.code = statusCode;
      } else {
        state.userCustodians = [];
        state.error = message;
        state.code = statusCode;
      }
    },

    [GetUserCustodianById.rejected]: (state, action) => {
      state.loading = false;
    },

    [GetCustodianAccountTable.pending]: (state, action) => {
      state.loading = true;
    },
    [GetCustodianAccountTable.fulfilled]: (
      state,
      { payload: { status, data, message, code } }
    ) => {
      state.loading = false;
      if ((code === 200 || status?.toUpperCase() === "SUCCESS") && data) {
        state.custAccData = data;
        state.message = message;
        state.status = status;
        state.code = code;
      } else {
        state.custAccData = [];
        state.error = message;
        state.status = status;
        state.code = code;
      }
    },

    [GetCustodianAccountTable.rejected]: (state, action) => {
      state.loading = false;
    },

    [GetUserCustodian.pending]: (state, action) => {
      state.loading = true;
    },
    [GetUserCustodian.fulfilled]: (
      state,
      { payload: { statusCode, data, message } }
    ) => {
      state.loading = false;
      if (statusCode === 200 && data) {
        state.userCust = data;
        state.message = message;
        state.code = statusCode;
      } else {
        state.userCust = [];
        state.error = message;
        state.code = statusCode;
      }
    },

    [GetUserCustodian.rejected]: (state, action) => {
      state.loading = false;
    },

    [UpdateUserCustodian.pending]: (state, action) => {
      state.loading = true;
    },
    [UpdateUserCustodian.fulfilled]: (
      state,
      { payload: { statusCode, data, message } }
    ) => {
      state.loading = false;
      if (statusCode === 200 && data) {
        customSuccessAlert(message);
        const { name, value } = data;
        const userCustIndex = state.userCust.findIndex(
          (item) => item.name === name
        );
        if (userCustIndex !== -1) {
          state.userCust[userCustIndex].value = value;
        }
        state.message = message;
        state.code = statusCode;
      } else {
        customFailAlert(message);
        state.error = message;
        state.code = statusCode;
      }
    },
    [UpdateUserCustodian.rejected]: (state, action) => {
      state.loading = false;
    },

    [GetAllUsersName.pending]: (state, action) => {
      state.loading = true;
    },
    [GetAllUsersName.fulfilled]: (
      state,
      { payload: { status, data, message, code } }
    ) => {
      state.loading = false;
      if ((code === 200 || status?.toUpperCase() === "SUCCESS") && data) {
        state.userNames = data;
        state.message = message;
        state.status = status;
        state.code = code;
      } else {
        state.userNames = [];
        state.error = message;
        state.status = status;
        state.code = code;
      }
    },
    [GetAllUsersName.rejected]: (state, action) => {
      state.loading = false;
    },

    [GetFileInfo.pending]: (state, action) => {
      state.loading = true;
    },
    [GetFileInfo.fulfilled]: (
      state,
      { payload: { status, error, message } }
    ) => {
      state.loading = false;
      if (error || status === 500) {
        state.error = message;
        state.status = status;
      } else {
        customSuccessAlert("File has been Downloaded");
      }
    },
    [GetFileInfo.rejected]: (state, action) => {
      state.loading = false;
    },

    [GetCustodianTable.pending]: (state, action) => {
      state.loading = true;
    },
    [GetCustodianTable.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.loading = false;

      if (statusCode === 200) {
        state.custodianData = data;
        state.message = message;
        state.status = status;
        state.code = statusCode;
      } else {
        state.custodianData = [];
        state.error = message;
        state.status = status;
        state.code = statusCode;
      }
    },
    [GetCustodianTable.rejected]: (state, action) => {
      state.loading = false;
    },

    [AddCustodians.pending]: (state, action) => {
      state.loading = true;
    },

    [AddCustodians.fulfilled]: (
      state,
      {
        payload: {
          status,
          message,
          code,
          custodianId,
          custodianName,
          custodianDesc,
          custodianIcon,
        },
      }
    ) => {
      state.loading = false;
      if (status) {
        customSuccessAlert(message);
        state.message = message;
        state.status = status;
        state.code = code;
        const existingCustodian = state.custodianData?.find(
          (custodian) => custodian?.custodianId === custodianId
        );
        if (!existingCustodian) {
          state.custodianData?.push({
            custodianId,
            custodianName,
            custodianDesc,
            custodianIcon,
          });
        }
      } else {
        customFailAlert(message);
        state.error = message;
        state.status = status;
        state.code = code;
      }
    },

    [AddCustodians.rejected]: (state, action) => {
      state.loading = false;
    },

    [CreateKYC.pending]: (state, action) => {
      state.loading = true;
    },
    [CreateKYC.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.loading = false;
      if (statusCode === 201 ||statusCode ===200) {
        customSuccessAlert(message);
        state.userKyc = data;
        state.message = message;
        state.status = status;
        state.code = statusCode;
      } else {
        state.userKyc = {};
        customFailAlert(message);
        state.error = message;
        state.status = status;
        state.code = statusCode;
      }
    },
    [CreateKYC.rejected]: (state, action) => {
      state.userKyc = {};
      state.loading = false;
    },

    [GetUserKyc.pending]: (state, action) => {
      state.loading = true;
    },
    [GetUserKyc.fulfilled]: (
      state,
      { payload: { data, message, statusCode } }
    ) => {
      state.loading = false;
      if (statusCode === 200 ) {
        state.userKyc = data;
        state.message = message;
      } else {
        state.userKyc = {};
        state.error = message;
      }
    },
    [GetUserKyc.rejected]: (state, action) => {
      state.userKyc = {};
      state.loading = false;
    },
    [DeleteKYC.pending]: (state, action) => {
      state.loading = true;
    },
    [DeleteKYC.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.loading = false;
      if (statusCode === 200) {
        customSuccessAlert(message);
        state.message = message;
        state.status = status;
        state.code = statusCode;
      } else {
        customFailAlert(message);
        state.error = message;
        state.status = status;
        state.code = statusCode;
      }
    },
    [DeleteKYC.rejected]: (state, action) => {
      state.loading = false;
    },

    [GetUsers.pending]: (state, action) => {
      state.loading = true;
    },
    [GetUsers.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.loading = false;

      if (statusCode === 200) {
        state.message = message;
        state.status = status;
        state.code = statusCode;
        state.users = data;
      } else {
        state.error = message;
        state.status = status;
        state.code = statusCode;
      }
    },
    [GetUsers.rejected]: (state, action) => {
      state.loading = false;
    },

    [GetApiPermission.pending]: (state, action) => {
      state.loading = true;
    },
    [GetApiPermission.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.loading = false;

      if (statusCode === 200) {
        state.message = message;
        state.status = status;
        state.code = statusCode;
        state.userPermissions = data;
      } else {
        state.error = message;
        state.status = status;
        state.code = statusCode;
      }
    },
    [GetApiPermission.rejected]: (state, action) => {
      state.loading = false;
    },

    [GetUserInfo.pending]: (state, action) => {
      state.loading = true;
    },
    [GetUserInfo.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.loading = false;

      if (statusCode === 200) {
        state.message = message;
        state.status = status;
        state.code = statusCode;
        state.userInformation = data;
      } else {
        state.error = message;
        state.status = status;
        state.code = statusCode;
      }
    },
    [GetUserInfo.rejected]: (state, action) => {
      state.loading = false;
    },

    

    [GetUserRoles.pending]: (state, action) => {
      state.loading = true;
    },
    [GetUserRoles.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.loading = false;

      if (statusCode === 200) {
        state.message = message;
        state.status = status;
        state.code = statusCode;
        state.roles = data;
      } else {
        state.error = message;
        state.status = status;
        state.code = statusCode;
      }
    },
    [GetUserRoles.rejected]: (state, action) => {
      state.loading = false;
    },

    [GetAllRoles.pending]: (state, action) => {
      state.loading = true;
    },
    [GetAllRoles.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.loading = false;

      if (statusCode === 200) {
        state.message = message;
        state.status = status;
        state.code = statusCode;
        state.allRoles = data;
      } else {
        state.error = message;
        state.status = status;
        state.code = statusCode;
      }
    },
    [GetAllRoles.rejected]: (state, action) => {
      state.loading = false;
    },

    [CreateSubRoleGroup.pending]: (state, action) => {
      state.loading = true;
    },
    [CreateSubRoleGroup.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.loading = false;

      if (statusCode === 201) {
        state.message = message;
        state.status = status;
        state.code = statusCode;
        state.roleGroups = [...state.roleGroups, data];
        customSuccessAlert("Role group created successfully");
      } else {
        state.error = message;
        state.status = status;
        state.code = statusCode;
        customFailAlert(message || "Failed to create role group");
      }
    },
    [CreateSubRoleGroup.rejected]: (state, action) => {
      state.loading = false;
      customFailAlert("Failed to create role group");
    },

    [GetRoleGroups.pending]: (state, action) => {
      state.loading = true;
    },
    [GetRoleGroups.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.loading = false;

      if (statusCode === 200) {
        state.message = message;
        state.status = status;
        state.code = statusCode;
        state.roleGroups = data;
      } else {
        state.error = message;
        state.status = status;
        state.code = statusCode;
      }
    },
    [GetRoleGroups.rejected]: (state, action) => {
      state.loading = false;
    },

    [GetRoleGroupPermissions.pending]: (state, action) => {
      state.loading = true;
    },
    [GetRoleGroupPermissions.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.loading = false;

      if (statusCode === 200) {
        state.message = message;
        state.status = status;
        state.code = statusCode;
        state.roleGroupPermissions = data;
      } else {
        state.error = message;
        state.status = status;
        state.code = statusCode;
      }
    },
    [GetRoleGroupPermissions.rejected]: (state, action) => {
      state.loading = false;
    },

    [UpdateRoleGroupPermissions.pending]: (state, action) => {
      state.loading = true;
    },
    [UpdateRoleGroupPermissions.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.loading = false;

      if (statusCode === 200) {
        state.message = message;
        state.status = status;
        state.code = statusCode;
        state.roleGroupPermissions = data;
        customSuccessAlert("Permissions updated successfully");
      } else {
        state.error = message;
        state.status = status;
        state.code = statusCode;
        customFailAlert(message || "Failed to update permissions");
      }
    },
    [UpdateRoleGroupPermissions.rejected]: (state, action) => {
      state.loading = false;
      customFailAlert("Failed to update permissions");
    },

    // Household Management
    [GetHouseholds.pending]: (state) => {
      state.householdsLoading = true;
    },
    [GetHouseholds.fulfilled]: (
      state,
      { payload: { statusCode, data, message } }
    ) => {
      state.householdsLoading = false;
      if (statusCode === 200 && Array.isArray(data)) {
        state.households = data;
        state.message = message;
      } else {
        state.households = [];
        state.error = message || "Failed to fetch households";
      }
    },
    [GetHouseholds.rejected]: (state, action) => {
      state.householdsLoading = false;
      state.households = [];
      state.error = action.error?.message || "Failed to fetch households";
    },
  },
});
export default userStatusReducer.reducer;
